import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronsLeft,
  ChevronsRight,
  LayoutDashboard,
  Tag,
  Box,
  FolderKanban,
  FileSearch,
  Lightbulb,
  FileText,
  Building2,
  Users,
  KeyRound,
  Plug,
  Coins,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { key: "dashboard", to: "/dashboard", icon: LayoutDashboard, ready: true },
  { key: "brands", to: "/brands", icon: Tag, ready: true },
  { key: "products", to: "/products", icon: Box, ready: true },
  { key: "projects", to: "/projects", icon: FolderKanban, ready: true },
  { key: "evidence", to: "/evidence", icon: FileSearch, ready: true },
  { key: "findings", to: "/findings", icon: Lightbulb, ready: true },
  { key: "reports", to: "/reports", icon: FileText, ready: true },
  // Phase 12 — Competitors removed from the top-level nav; competitor
  // management lives inside Brand → Competitors and Product → Competitors.
  // The /competitors route is still reachable for old bookmarks.
];

const SETTINGS_ITEMS = [
  { key: "tenant", to: "/settings/tenant", icon: Building2, ready: true },
  { key: "users", to: "/settings/users", icon: Users, ready: true },
  { key: "credits", to: "/settings/credits", icon: Coins, ready: true },
  { key: "llm", to: "/settings/llm", icon: KeyRound, ready: true },
  { key: "integrations", to: "/settings/integrations", icon: Plug, ready: true },
];

function NavItem({ to, icon: Icon, label, collapsed, ready }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all",
          isActive
            ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200"
            : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
      {!collapsed && (
        <span className="flex-1 truncate">{label}</span>
      )}
      {!collapsed && !ready && (
        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Soon
        </span>
      )}
    </NavLink>
  );
}

export function Sidebar({ collapsed, onToggle }) {
  const { t } = useTranslation("nav");
  const location = useLocation();

  // While on /settings/* we want both sections visible; nothing collapses.
  const settingsActive = location.pathname.startsWith("/settings");

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "sticky top-0 z-30 flex h-screen flex-shrink-0 flex-col self-start overflow-y-auto border-r border-slate-200 bg-slate-50/80 backdrop-blur-xl transition-[width] duration-300 ease-out",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <div className={cn(
        "border-b border-slate-200",
        collapsed
          ? "flex flex-col items-center gap-2 px-2 py-3"
          : "flex h-16 items-center justify-between px-3"
      )}>
        <Link
          to="/dashboard"
          data-testid="brand-link"
          className={cn(
            "flex items-center gap-2 overflow-hidden",
            collapsed ? "justify-center w-full" : ""
          )}
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white shadow-sm">
            <span className="font-display text-sm font-semibold tracking-tighter">E</span>
          </div>
          {!collapsed && (
            <span className="font-display text-base font-semibold tracking-tight text-slate-900">
              EvidenceLab
            </span>
          )}
        </Link>
        {/* Toggle — always rendered. Shows ChevronsLeft when expanded, ChevronsRight when
            collapsed. Anchored at the top of the icon rail in collapsed mode. */}
        <button
          type="button"
          data-testid={collapsed ? "sidebar-expand" : "sidebar-collapse"}
          onClick={onToggle}
          title={t(collapsed ? "tooltips.expand" : "tooltips.collapse")}
          aria-label={t(collapsed ? "tooltips.expand" : "tooltips.collapse")}
          className={cn(
            "rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700",
            collapsed ? "flex h-8 w-8 items-center justify-center" : "p-1"
          )}
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4" strokeWidth={1.75} />
          ) : (
            <ChevronsLeft className="h-4 w-4" strokeWidth={1.75} />
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4">
        {!collapsed && (
          <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {t("sections.main")}
          </p>
        )}
        <div className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <NavItem
              key={item.key}
              to={item.to}
              icon={item.icon}
              label={t(`items.${item.key}`)}
              collapsed={collapsed}
              ready={item.ready}
            />
          ))}
        </div>

        <div className="mt-6">
          {!collapsed && (
            <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {t("sections.settings")}
            </p>
          )}
          <div className="space-y-1">
            {SETTINGS_ITEMS.map((item) => (
              <NavItem
                key={item.key}
                to={item.to}
                icon={item.icon}
                label={t(`items.${item.key}`)}
                collapsed={collapsed}
                ready={item.ready}
              />
            ))}
          </div>
        </div>
      </nav>

      {collapsed && (
        <button
          type="button"
          data-testid="sidebar-expand"
          onClick={onToggle}
          aria-label={t("tooltips.expand")}
          className="m-2 flex items-center justify-center rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <ChevronsRight className="h-4 w-4" strokeWidth={1.75} />
        </button>
      )}

      {!collapsed && (
        <div className="border-t border-slate-200 px-3 py-3 text-[11px] text-slate-400">
          <span className="font-display tracking-wide">Phase 2 · {settingsActive ? "Settings" : "Workspace"}</span>
        </div>
      )}
    </aside>
  );
}
